import { css } from 'aphrodite';
import { useMutation } from '@tanstack/react-query';
import { parseAsString, useQueryState } from 'nuqs';
import * as React from 'react';
import { Prompt, useLocation } from 'react-router-dom';
import { IAIAgentTemplate } from '../../../../extViewmodels/sdk/models/AI';
import { useAIAgentTemplatesQuery } from '../../../../queries/AIAgentTemplate';
import { Alert, AlertDescription, AlertTitle } from '../../../../web/components-v2/Alert';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { BasicPage } from '../../BasicPage';
import { CoachLabCall } from './CoachLabCall';
import { CoachLabRealCall } from './CoachLabRealCall';
import { CoachLabSetup, type RealCallRef } from './CoachLabSetup';
import {
	AfterTheCallTip,
	DuringTheCallTip,
	FeedbackTip,
	HardOnPurposeTip,
	HowItWorksTip,
	RealCallTip,
} from './Guidance';
import { useSessionBatch } from './labBatch';
import { SessionBatchPanel } from './SessionBatchPanel';
import { styles } from './styles';
import { type ICoachLabCallSetup, useCoachLabCall } from './useCoachLabCall';
import { useCoachLabTransport } from './useCoachLabTransport';

export const CoachLab = () => {
	const templates = useAIAgentTemplatesQuery();
	const location = useLocation();
	const call = useCoachLabCall();
	const [active, setActive] = React.useState<{ setup: ICoachLabCallSetup; coach: IAIAgentTemplate } | null>(null);
	const [callNumber, setCallNumber] = React.useState(0);
	const [callSaved, setCallSaved] = React.useState(false);
	const batch = useSessionBatch();
	// A "Give coach feedback" link lands here with the coaching conversation id.
	const [linkedConversation, setLinkedConversation] = useQueryState('convo', parseAsString);
	const [review, setReview] = React.useState<{ conversationId: string; coach: IAIAgentTemplate } | null>(null);
	const [reviewUnsaved, setReviewUnsaved] = React.useState(false);
	const [reviewError, setReviewError] = React.useState<string | null>(null);
	const transport = useCoachLabTransport();
	const hasUnsavedWork =
		batch.notDownloaded > 0 || (review ? reviewUnsaved : !!active && call.state.turns.length > 0 && !callSaved);

	// A call log link names the phone call, which carries the id of the coaching conversation on it.
	const findConversation = useMutation({
		mutationFn: (phoneCallId: string) => transport.phoneCall(phoneCallId, AbortSignal.timeout(20000)),
	});

	React.useEffect(() => {
		if (!hasUnsavedWork) return;
		const warn = (event: BeforeUnloadEvent) => event.preventDefault();
		window.addEventListener('beforeunload', warn);
		return () => window.removeEventListener('beforeunload', warn);
	}, [hasUnsavedWork]);

	const pageTop = React.useRef<HTMLDivElement>(null);
	// AIDA scrolls an inner container rather than the window, so bring the page's top into view after the next render.
	const scrollToTop = () => requestAnimationFrame(() => pageTop.current?.scrollIntoView({ block: 'start' }));

	const start = (setup: ICoachLabCallSetup, coach: IAIAgentTemplate) => {
		scrollToTop();
		setActive({ coach, setup });
		setCallNumber(n => n + 1);
		setCallSaved(false);
		call.start(setup);
	};

	const newCall = () => {
		scrollToTop();
		call.reset();
		setActive(null);
		setReview(null);
		setReviewUnsaved(false);
		if (linkedConversation) setLinkedConversation(null);
	};

	const openReview = async (ref: RealCallRef, coach: IAIAgentTemplate) => {
		setReviewError(null);
		let conversationId = ref.conversationId;
		if (!conversationId && ref.phoneCallId) {
			try {
				conversationId = (await findConversation.mutateAsync(ref.phoneCallId))?.aiConversationId;
			} catch {
				setReviewError('Could not look up that call. Check the link and try again.');
				return;
			}
			if (!conversationId) {
				setReviewError('The rep was not coached on that call, so it has no coach cards to review.');
				return;
			}
		}
		if (!conversationId) return;
		setReviewUnsaved(false);
		setReview({ coach, conversationId });
		scrollToTop();
	};

	return (
		<BasicPage title='Coach Lab' appearance='light'>
			<Prompt
				when={hasUnsavedWork}
				// Coach Lab rewrites its own address (the ?convo= link), which must not count as leaving the page.
				message={next =>
					next.pathname === location.pathname
						? true
						: "You have Coach Lab calls that aren't saved or downloaded yet. Leave anyway?"
				}
			/>
			<div ref={pageTop} />
			<div className={css(styles.page)}>
				<div className={css(styles.main)}>
					{review ? (
						<CoachLabRealCall
							key={review.conversationId}
							conversationId={review.conversationId}
							sandboxCoach={review.coach}
							onDone={newCall}
							onSave={session => batch.add({ ...session, kind: 'Real call' })}
							onUnsavedChange={setReviewUnsaved}
						/>
					) : active ? (
						<CoachLabCall
							key={callNumber}
							call={call}
							coach={active.coach}
							onCallAgain={() => start(active.setup, active.coach)}
							onNewCall={newCall}
							onSave={session => {
								batch.add({ ...session, kind: 'Role-play' });
								setCallSaved(true);
							}}
						/>
					) : (
						<>
							<p className={css(styles.intro)}>
								Test a version of the AI coach by doing what a rep does: you dial, you talk, and the coach shows you a
								card every turn. Tell us what the coach should do differently, we turn that into changes to your
								version, and you call again.
							</p>
							{templates.isPending ? (
								<LoadingSpinner />
							) : templates.isError ? (
								<Alert tone='danger'>
									<AlertTitle>Could not load the coaching templates</AlertTitle>
									<AlertDescription>Refresh the page to try again.</AlertDescription>
								</Alert>
							) : (
								<>
									{reviewError ? (
										<Alert tone='warning'>
											<AlertTitle>{reviewError}</AlertTitle>
										</Alert>
									) : null}
									<CoachLabSetup
										templates={templates.data ?? []}
										initialRealCall={linkedConversation ?? undefined}
										onStart={start}
										onReview={openReview}
										opening={findConversation.isPending}
									/>
								</>
							)}
						</>
					)}
				</div>
				<aside className={css(styles.aside)}>
					<SessionBatchPanel batch={batch} />
					{review ? <RealCallTip /> : active ? <DuringTheCallTip /> : <HowItWorksTip />}
					<FeedbackTip />
					<HardOnPurposeTip />
					<AfterTheCallTip />
				</aside>
			</div>
		</BasicPage>
	);
};
